import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ELinxHistoryItem, ELinxHistoryRequest } from 'vopay';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import useFileDownload from '@/hooks/useFiledDownload';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import type { ExportType } from '@/components/Elinx/ElinxHistory/ElinxHistoryExport';

const EXPORT_TABLE_ID = 'export-elinx-history-table';

/**
 * Shared by ElinxHistory.tsx (the /elinx-history page) and ElinxHistoryTable.tsx (the same export
 * flow embedded in the Client Account/Contact "Activity" tab). Owns the whole export: it fetches
 * the export dataset, downloads JSON directly, and for every other format builds the file by
 * scraping a hidden, off-screen #export-elinx-history-table DOM node. Callers only build the
 * (caller-specific) request and surface errors.
 *
 * Returns a coupled pair:
 *   - `itemsToExport` — the data the caller must render in that hidden <Table> (spread into its
 *     `rows`). The table exists only to be scraped; these items are what populate it.
 *   - `runExport(format, request)` — fetches, then either downloads JSON directly or pushes the
 *     rows into `itemsToExport` and uses `flushSync` to commit that render to the DOM synchronously
 *     before scraping it. The synchronous commit is the documented React pattern for handing the
 *     DOM to non-React code (useFileDownload's table readers), so there's no need to poll/observe
 *     the DOM for readiness. Throws on a failed fetch, which the caller catches and surfaces.
 */
export default function useElinxHistoryExport() {
  const [, , , fetchElinxHistoryExport] = useFetchELinxHistory({});

  const {
    downloadJson,
    downloadPdfTable,
    downloadCsvFile,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();

  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const alertDialog = useAlertDialog();

  const [itemsToExport, setItemsToExport] = useState<ELinxHistoryItem[]>([]);

  const runExport = async (format: ExportType, request: ELinxHistoryRequest) => {
    const res = await fetchElinxHistoryExport(request);

    if (!res.Success) throw new Error(res.ErrorMessage);

    const items = Object.values(res.ELinxRequests || {});

    if (format === 'json') {
      downloadJson(items, t('title'));

      return;
    }

    // flushSync commits the hidden Table's new rows to the DOM synchronously, so the scrape below
    // reads the freshly fetched data rather than the previous export's (or empty) DOM.
    flushSync(() => setItemsToExport(items));

    const table = document.getElementById(EXPORT_TABLE_ID);

    // Defensive: if the committed DOM doesn't reflect the data (missing table / unexpected Table
    // shape), surface an error instead of silently exporting a blank file — the ID-10670 symptom.
    if (!table || table.querySelectorAll('tbody tr').length !== items.length) {
      alertDialog.error(t('ErrorMessages.error'), t('ErrorMessages.fetchingError'));

      return;
    }

    switch (format) {
      case 'pdf':
        downloadPdfTable(EXPORT_TABLE_ID, t('title'));
        break;
      case 'csv':
        downloadCsvFile(EXPORT_TABLE_ID, t('title'));
        break;
      case 'xls':
        downloadXls(EXPORT_TABLE_ID, t('title'));
        break;
      case 'doc':
        downloadDoc(EXPORT_TABLE_ID, t('title'));
        break;
      case 'text':
        downloadTxt(EXPORT_TABLE_ID, t('title'));
        break;
      case 'xml':
        downloadXml(EXPORT_TABLE_ID, t('title'));
        break;
      case 'print':
        printFile(EXPORT_TABLE_ID);
        break;
      default:
        break;
    }
  };

  return { itemsToExport, runExport };
}
